import Menu from '@/global/components/Menu'
import { TextField } from '@/global/components/TextField'
import { Button as ButtonUI } from '@/global/components/ui/button'
import { cn } from '@/lib/utils'
import {
    CheckIcon,
    CloudDownloadIcon,
    PencilIcon,
    PinIcon,
    Trash2Icon,
} from 'lucide-react'
import React from 'react'
import { Button } from '../../Button'
import { filtroVazio } from '../hooks/useDefaultFilterModel'
import type useFilterPresets from '../hooks/useFilterPresets'
import FilterModelPro, {
    GridFilterItemPro,
    GridFilterModelPro,
} from '../utils/filterModelPro'

/**
 * O destaque padrão do menu preenche a linha com `primary`, que aqui brigaria
 * com os botões coloridos de cada preset — o realce fica só no fundo.
 */
const ITEM = 'h-10 focus:bg-accent focus:text-foreground'

/** Botão de ação dentro da linha do preset. */
function AcaoPreset({
    className,
    ...props
}: React.ComponentProps<typeof ButtonUI>) {
    return (
        <ButtonUI
            type="button"
            variant="ghost"
            size="icon"
            className={cn('size-8 shrink-0 rounded-full', className)}
            {...props}
        />
    )
}

export default function FilterPreset({
    onFilterModelChange,
    presets,
    filtroPadrao,
}: {
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
    /** Instância do painel, compartilhada com o botão de salvar filtro. */
    presets: ReturnType<typeof useFilterPresets>
    /** Padrão da tela, listado fixo no topo, sem renomear nem excluir. */
    filtroPadrao?: GridFilterModelPro
}) {
    const { filtros, atualizarPreset, excluirPreset, isLoading } = presets

    /** `id` do preset em edição — enquanto houver um, o menu não fecha ao clicar. */
    const [editando, setEditando] = React.useState<number | null>(null)
    const [nome, setNome] = React.useState('')

    function carregarPreset(items: GridFilterItemPro[]) {
        onFilterModelChange(filtroVazio)
        // O modelo precisa ser esvaziado antes de receber o do preset, senão as
        // linhas antigas do painel se misturam com as novas.
        setTimeout(() => {
            onFilterModelChange({
                ...filtroVazio,
                ...new FilterModelPro({ items }).filterModel,
            })
        }, 100)
    }

    function salvarNome(id: number) {
        atualizarPreset(id, nome)
        setEditando(null)
    }

    return (
        <Menu.Root onOpenChange={aberto => !aberto && setEditando(null)}>
            <Menu.Trigger>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={isLoading}
                    startIcon={<CloudDownloadIcon />}
                >
                    Carregar filtro
                </Button>
            </Menu.Trigger>
            <Menu.List className="max-h-[200px] w-[250px] overflow-y-auto">
                {!filtros.length && !filtroPadrao?.items.length && (
                    <Menu.Item disabled>
                        <em>Nenhum filtro salvo</em>
                    </Menu.Item>
                )}
                {!!filtroPadrao?.items.length && (
                    <Menu.Item
                        className={cn(ITEM, 'gap-1')}
                        onSelect={() => carregarPreset(filtroPadrao.items)}
                    >
                        <PinIcon className="mr-1 size-4 text-muted-foreground" />
                        <span title="Carregar filtro padrão da tela">
                            Padrão
                        </span>
                    </Menu.Item>
                )}
                {filtros.map(filtro => {
                    const emEdicao = editando === filtro.id

                    return (
                        <Menu.Item
                            key={filtro.id}
                            className={cn(ITEM, 'gap-1')}
                            // Em edição o clique é do campo de nome, não do item.
                            onSelect={evento => {
                                if (emEdicao) return evento.preventDefault()
                                carregarPreset(filtro.items)
                            }}
                            // O Radix move o foco para o item ao passar o mouse
                            // — tiraria o cursor de dentro do campo de nome.
                            onPointerMove={evento => {
                                if (emEdicao) evento.preventDefault()
                            }}
                        >
                            {emEdicao ? (
                                <>
                                    <TextField
                                        variant="outlined"
                                        autoFocus
                                        size="small"
                                        unHelperText
                                        value={nome}
                                        maxLength={40}
                                        className="flex-1"
                                        inputClassName="h-8 text-[0.85rem]"
                                        onChange={e => setNome(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter')
                                                salvarNome(filtro.id)
                                            else e.stopPropagation()
                                        }}
                                    />
                                    <AcaoPreset
                                        title="Salvar"
                                        onClick={() => salvarNome(filtro.id)}
                                    >
                                        <CheckIcon className="text-success" />
                                    </AcaoPreset>
                                </>
                            ) : (
                                <>
                                    <span
                                        title={`Carregar ${filtro.nome}`}
                                        className="relative max-w-[160px] flex-1 overflow-hidden"
                                    >
                                        {filtro.nome}
                                    </span>
                                    <AcaoPreset
                                        title="Alterar nome"
                                        onClick={e => {
                                            e.stopPropagation()
                                            setNome(filtro.nome)
                                            setEditando(filtro.id)
                                        }}
                                    >
                                        <PencilIcon className="text-primary" />
                                    </AcaoPreset>
                                </>
                            )}
                            <AcaoPreset
                                title="Excluir"
                                onClick={e => {
                                    e.stopPropagation()
                                    excluirPreset(filtro.id)
                                }}
                            >
                                <Trash2Icon className="text-error" />
                            </AcaoPreset>
                        </Menu.Item>
                    )
                })}
            </Menu.List>
        </Menu.Root>
    )
}
